// Shared building blocks for the item detail popups (initiatives, library,
// platforms), so all three read and feel the same: a glance panel of key
// facts, a panel of labelled answers, a details grid.
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

export function formatDetailDate(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** Strips scripts, inline handlers and javascript: links from SharePoint
 *  rich text before it's rendered. */
export function sanitizeHtml(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script, style, iframe, object, embed").forEach((n) => n.remove());
  // Line breaks are kept on screen (white-space: pre-wrap), so drop the
  // formatting-only newlines that sit between tags.
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const gaps: Node[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (/^\s*\n\s*$/.test(n.nodeValue || "")) gaps.push(n);
  }
  gaps.forEach((n) => n.parentNode?.removeChild(n));
  doc.querySelectorAll<HTMLElement>("*").forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on") || name === "style") {
        el.removeAttribute(attr.name);
        continue;
      }
      if (name === "href" || name === "src") {
        const v = attr.value.trim().toLowerCase();
        if (v.startsWith("javascript:") || v.startsWith("data:text/html")) {
          el.removeAttribute(attr.name);
        }
      }
    }
    if (el.tagName === "A") {
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer");
    }
  });
  return doc.body.innerHTML;
}

export function htmlToPlainText(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  doc.querySelectorAll("p, div, li, tr").forEach((el) => el.append("\n"));
  return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}

const BULLET = /^\s*(?:[-*•–]|\d+[.)])\s+/;

/**
 * Renders free text the way the submitter laid it out: blank lines split
 * paragraphs, and a run of short lines (or lines that start with a bullet or
 * number) becomes a real list.
 */
export function RichText({ text }: { text: string }) {
  const blocks = text
    .replace(/\r\n/g, "\n")
    .split(/\n\s*\n/)
    .map((b) => b.split("\n").map((l) => l.trim()).filter(Boolean))
    .filter((lines) => lines.length > 0);

  return (
    <div className="ini-rich">
      {blocks.map((lines, i) => {
        const bulleted = lines.filter((l) => BULLET.test(l)).length;
        const allShort = lines.every((l) => l.length <= 120);
        const lead = lines[0].endsWith(":") ? lines[0] : null;
        const rest = lead ? lines.slice(1) : lines;
        const isList =
          rest.length >= 2 && (bulleted >= rest.length - 1 || (allShort && lines.length >= 3));
        if (isList) {
          return (
            <div key={i}>
              {lead ? <p>{lead}</p> : null}
              <ul>
                {rest.map((l, j) => (
                  <li key={j}>{l.replace(BULLET, "")}</li>
                ))}
              </ul>
            </div>
          );
        }
        return <p key={i}>{lines.join("\n")}</p>;
      })}
    </div>
  );
}

/** Sanitized SharePoint rich text, styled to match RichText. */
export function RichHtml({ html }: { html: string }) {
  return <div className="ini-rich ini-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />;
}

/** One labelled answer: label (with icon) on top, the answer below. Renders
 *  nothing when there's no content. `action` sits at the right of the label. */
export function Row({
  icon: Icon,
  label,
  text,
  children,
  action,
}: {
  icon: LucideIcon;
  label: string;
  text?: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  const body = children ?? (text && text.trim() ? <RichText text={text} /> : null);
  if (!body) return null;
  return (
    <div className="ini-row">
      <dt>
        <Icon size={14} aria-hidden />
        <span className="ini-row-label">{label}</span>
        {action ? <span className="ini-row-action">{action}</span> : null}
      </dt>
      <dd>{body}</dd>
    </div>
  );
}

/** A glance-panel cell: small uppercase label over a short value. */
export function GlanceCell({
  icon: Icon,
  label,
  children,
  wide,
}: {
  icon?: LucideIcon;
  label: string;
  children: ReactNode;
  /** Give a cell with a long value more of the row. */
  wide?: boolean;
}) {
  return (
    <div className={`ini-glance-cell${wide ? " ini-glance-cell--wide" : ""}`}>
      <span className="ini-glance-label">
        {Icon ? <Icon size={12} aria-hidden /> : null} {label}
      </span>
      <span className="ini-glance-value">{children}</span>
    </div>
  );
}

export function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  if (!value) return null;
  return (
    <div className="ini-detail">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
